import React, { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';

interface GlassClockProps {
  dateString?: string; // e.g., "Dec 10"
  timeString?: string; // e.g., "09:19:31 PM"
  dayString?: string; // e.g., "Fri, Dec 13"
}

export const GlassClock: React.FC<GlassClockProps> = React.memo(({ 
  dateString,
  timeString,
  dayString,
}) => {
  const shouldSelfUpdate = !dateString && !timeString && !dayString;
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    if (!shouldSelfUpdate) return undefined;
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, [shouldSelfUpdate]);

  const computed = useMemo(() => {
    if (!shouldSelfUpdate) return null;
    return {
      dateString: now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      timeString: now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      dayString: now.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
    };
  }, [now, shouldSelfUpdate]);

  const shownDay = dayString ?? dateString ?? computed?.dayString ?? '—';
  const shownTime = timeString ?? computed?.timeString ?? '—';

  return (
    <div className="relative overflow-hidden rounded-lg">
      {/* Gradient (static) */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-100 transition duration-500"></div>
      
      {/* Main Content */}
      <div className="relative flex items-center gap-3 px-4 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg min-w-0 whitespace-nowrap">
        <Calendar className="w-3.5 h-3.5 text-zinc-500 dark:text-gray-400 flex-shrink-0" />

        {/* Day */}
        <span className="text-zinc-700 dark:text-gray-300 text-xs font-medium tracking-wider truncate min-w-0">
          {shownDay}
        </span>

        {/* Blue Separator Dot */}
        <span className="w-1 h-1 bg-blue-600 dark:bg-blue-500 rounded-full flex-shrink-0" />

        {/* Time */}
        <span className="text-zinc-600 dark:text-gray-400 text-sm tabular-nums tracking-wide font-mono truncate min-w-0">
          {shownTime}
        </span>
      </div>
    </div>
  );
});

export default GlassClock;
